import React, { useEffect, useRef, useState } from 'react';
import {
    BASE_FONT_SIZE,
    BASE_OUTLINE_WIDTH,
    BASE_PLAYER_HEIGHT,
    DEFAULT_SUBTITLE_SETTINGS,
    MAX_WIDTH_SUBTITLES_LANDSCAPE,
    NON_LANDSCAPE_COEF_FONT_SIZE
} from 'engage/features/SubtitlesEditor/const';
import useWindowSize from './useWindowSize';
import { checkFontLoaded, VIDEO_ORIENTATIONS } from 'engage/utils/SimulatedItemsUtility';

const MAX_WIDTH_TEST_STRING = 'LOREM IPSUM DOLOR SIT AMET CONSECTETUR DAPIBUS NAM';
const DEFAULT_MAX_SYMBOLS = 50;

export const useSubtitlesStyles = ({ playerRef, subtitlesStyles, isFullScreen, mixedOrientation, videoMaxDimensions }) => {

    const [customStyles, setCustomStyles] = useState({
        fontSize: `${BASE_FONT_SIZE}px`,
    });
    const [maxSymbolsInString, setMaxSymbolsInString] = useState(DEFAULT_MAX_SYMBOLS);

    const { width } = useWindowSize();

    const subtitlesStylesRef = useRef(DEFAULT_SUBTITLE_SETTINGS);
    const videoMaxDimensionsRef = useRef(videoMaxDimensions);
    const checkTimeoutRef = useRef(null);

    const getMaxSymbolsInString = (maxSubtitlesWidth, styles) => {
        const testSpan = document.createElement('span');
        testSpan.style.fontFamily = styles.fontFamily;
        testSpan.style.fontSize = styles.fontSize;
        if (styles?.textShadow) {
            testSpan.style.textShadow = styles.textShadow;
        }
        testSpan.style.width = 'fit-content';
        testSpan.style.visibility = 'hidden';
        testSpan.style.position = 'absolute';

        document.body.appendChild(testSpan);
        let maxSymbolsInString = MAX_WIDTH_TEST_STRING.length;
        for (let i = 10; i < MAX_WIDTH_TEST_STRING.length; i++) {
            testSpan.textContent = MAX_WIDTH_TEST_STRING.slice(0, i);
            if (testSpan.offsetWidth >= maxSubtitlesWidth) {
                maxSymbolsInString = i;
                break;
            }
        }
        document.body.removeChild(testSpan);
        return maxSymbolsInString;
    };

    const getCustomStyles = async (tries = 0) => {
        const { fontSize, textColor: color, backgroundColor, textShadow, textOutlineColor } = subtitlesStylesRef.current;

        let video = null;
        if (playerRef?.current) {
            video = playerRef?.current?.querySelector('video');
        } else {
            const videos = document.querySelectorAll('video');
            video = Array.from(videos)?.find(item => item.offsetWidth > 0);
        }

        if (video?.videoWidth > 0) {
            let fontCoeff = 1;
            let maxSubtitlesWidth = video.offsetWidth * MAX_WIDTH_SUBTITLES_LANDSCAPE;
            let contentHeight = video.offsetHeight;
            if (mixedOrientation === VIDEO_ORIENTATIONS.square || (!mixedOrientation && video.videoHeight / video?.videoWidth >= 1)) {
                fontCoeff = NON_LANDSCAPE_COEF_FONT_SIZE;
                if (mixedOrientation) {
                    maxSubtitlesWidth = video.offsetHeight * videoMaxDimensions?.width;
                } else {
                    const heightScale = video.offsetHeight / video.videoHeight;
                    maxSubtitlesWidth = heightScale * video.videoWidth;
                }
            }

            if (videoMaxDimensionsRef?.current && videoMaxDimensionsRef?.current?.width > 1) {
                contentHeight = video.offsetWidth / videoMaxDimensionsRef.current.width;
            }
            const font = ((contentHeight * fontSize) / BASE_PLAYER_HEIGHT) * fontCoeff;

            const fontData = subtitlesStylesRef?.current?.font ?? {};
            try {
                await checkFontLoaded(fontData);
            } catch (e) {
                console.log('Error while loading font for subtitles', e);
            }

            const { name: fontFamilyName } = fontData;
            const fontFamily = fontFamilyName ? `'${fontFamilyName}'` : undefined;

            const styles = { ...(fontFamily && { fontFamily }), color, fontSize: `${font}px` };

            if (backgroundColor) {
                styles.backgroundColor = backgroundColor;
            } else if (textShadow) {
                styles.textShadow = '0px 2px 4px rgba(0, 0, 0, 0.75)';
            } else if (textOutlineColor) {
                const outlineWidth = (fontSize / BASE_FONT_SIZE) * BASE_OUTLINE_WIDTH;
                // need to define border size
                styles.webkitTextStroke = `${outlineWidth}px ${textOutlineColor}`;
                styles.paintOrder = 'stroke fill';
            }
            setMaxSymbolsInString(getMaxSymbolsInString(maxSubtitlesWidth, styles));

            setCustomStyles(styles);

            checkTimeoutRef.current = null;
            return;
        }

        if (typeof tries === 'number' && tries < 10) {
            checkTimeoutRef.current = setTimeout(() => {
                let newTries = tries;
                // Do not increase tries if video is not ready yet (buffering)
                if (video?.readyState >= HTMLMediaElement.HAVE_METADATA) {
                    newTries = tries + 1;
                }
                getCustomStyles(newTries);
            }, 250);
        }
    };

    useEffect(() => {
        if (subtitlesStyles) {
            subtitlesStylesRef.current = subtitlesStyles;
            if (!checkTimeoutRef.current) {
                getCustomStyles();
            }
        }
    }, [subtitlesStyles]);

    useEffect(() => {
        if (videoMaxDimensions && (videoMaxDimensionsRef.current?.width !== videoMaxDimensions?.width || videoMaxDimensionsRef.current?.height !== videoMaxDimensions?.height)) {
            videoMaxDimensionsRef.current = videoMaxDimensions;
            if (!checkTimeoutRef.current) {
                getCustomStyles();
            }
        }
    }, [videoMaxDimensions])

    useEffect(() => {
        const timeout = setTimeout(() => {
            getCustomStyles();
        }, 100);
        return () => clearTimeout(timeout);
    }, [width, isFullScreen]);

    return { customStyles, maxSymbolsInString };
}